/**
 * RLA Dashboard — E2E-Verschlüsselungs-Utilities
 *
 * Verwendet Web Crypto API:
 * - ECDH P-256 für Key Exchange
 * - AES-256-GCM für Nachrichtenverschlüsselung
 * - PBKDF2 für Password-basierte Key Derivation (Private Key Schutz)
 */

// ============================================================
// KEY GENERATION & MANAGEMENT
// ============================================================

/** Generiert ein neues ECDH P-256 Schlüsselpaar */
export async function generateKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true, // extractable
    ['deriveKey', 'deriveBits'],
  );
}

/** Exportiert einen Public Key als JWK */
export async function exportPublicKey(key: CryptoKey): Promise<JsonWebKey> {
  return crypto.subtle.exportKey('jwk', key);
}

/** Importiert einen Public Key aus JWK */
export async function importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    [],
  );
}

/** Exportiert einen Private Key als JWK */
export async function exportPrivateKey(key: CryptoKey): Promise<JsonWebKey> {
  return crypto.subtle.exportKey('jwk', key);
}

/** Importiert einen Private Key aus JWK */
export async function importPrivateKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits'],
  );
}

// ============================================================
// PASSWORD-BASED KEY WRAPPING (Private Key Schutz)
// ============================================================

/** Leitet einen AES-Key vom Passwort ab (für Private Key Verschlüsselung) */
async function deriveKeyFromPassword(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: 600000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Verschlüsselt den Private Key mit einem vom Passwort abgeleiteten Key */
export async function encryptPrivateKey(
  privateKey: CryptoKey,
  password: string,
): Promise<{ encrypted: Uint8Array; iv: Uint8Array; salt: Uint8Array }> {
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrappingKey = await deriveKeyFromPassword(password, salt);
  const jwk = await exportPrivateKey(privateKey);
  const data = new TextEncoder().encode(JSON.stringify(jwk));
  const encrypted = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, wrappingKey, data));
  return { encrypted, iv, salt };
}

/** Entschlüsselt den Private Key mit dem Passwort */
export async function decryptPrivateKey(
  encrypted: Uint8Array,
  iv: Uint8Array,
  salt: Uint8Array,
  password: string,
): Promise<CryptoKey> {
  const wrappingKey = await deriveKeyFromPassword(password, salt);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, wrappingKey, encrypted as BufferSource);
  const jwk = JSON.parse(new TextDecoder().decode(decrypted)) as JsonWebKey;
  return importPrivateKey(jwk);
}

// ============================================================
// ECDH KEY EXCHANGE (für DMs)
// ============================================================

/** Leitet einen gemeinsamen AES-Key aus ECDH ab (für DMs) */
export async function deriveSharedKey(
  myPrivateKey: CryptoKey,
  theirPublicKey: CryptoKey,
): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    { name: 'ECDH', public: theirPublicKey },
    myPrivateKey,
    { name: 'AES-GCM', length: 256 },
    true, // extractable for wrapping
    ['encrypt', 'decrypt'],
  );
}

// ============================================================
// GROUP KEY MANAGEMENT
// ============================================================

/** Generiert einen zufälligen AES-256 Gruppen-Key */
export async function generateGroupKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

/** Wraps (verschlüsselt) einen Gruppen-Key für ein bestimmtes Mitglied */
export async function wrapGroupKey(
  groupKey: CryptoKey,
  myPrivateKey: CryptoKey,
  memberPublicKey: CryptoKey,
): Promise<{ wrappedKey: Uint8Array; iv: Uint8Array }> {
  const sharedKey = await deriveSharedKey(myPrivateKey, memberPublicKey);
  const rawGroupKey = await crypto.subtle.exportKey('raw', groupKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, sharedKey, rawGroupKey);
  return { wrappedKey: new Uint8Array(encrypted), iv };
}

/** Unwraps (entschlüsselt) einen Gruppen-Key */
export async function unwrapGroupKey(
  wrappedKey: Uint8Array,
  iv: Uint8Array,
  myPrivateKey: CryptoKey,
  senderPublicKey: CryptoKey,
): Promise<CryptoKey> {
  const sharedKey = await deriveSharedKey(myPrivateKey, senderPublicKey);
  const rawKey = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, sharedKey, wrappedKey as BufferSource);
  return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

// ============================================================
// MESSAGE ENCRYPTION/DECRYPTION
// ============================================================

/** Verschlüsselt eine Nachricht mit AES-256-GCM */
export async function encryptMessage(
  plaintext: string,
  key: CryptoKey,
): Promise<{ ciphertext: Uint8Array; iv: Uint8Array }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(plaintext);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, data);
  return { ciphertext: new Uint8Array(encrypted), iv };
}

/** Entschlüsselt eine Nachricht */
export async function decryptMessage(
  ciphertext: Uint8Array,
  iv: Uint8Array,
  key: CryptoKey,
): Promise<string> {
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, ciphertext as BufferSource);
  return new TextDecoder().decode(decrypted);
}

// ============================================================
// HELPERS
// ============================================================

export function toBase64(arr: Uint8Array): string {
  return btoa(String.fromCharCode(...arr));
}

export function fromBase64(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}
